import React, { useState } from 'react';
import Papa from 'papaparse';
import axios from 'axios';

const apiUrl = process.env.REACT_APP_API_URL;

const VALID_TYPES = [
  'single_choice', 'multiple_choice', 'true_false',
  'short_text', 'long_answer', 'fill_blank', 'numerical',
];

const TEMPLATE_HEADERS = [
  'question_type', 'question_text', 'marks', 'negative_marks', 'difficulty', 'explanation',
  'options', 'correct', 'accepted_answers', 'case_sensitive', 'numerical_value', 'numerical_tolerance',
];

const TEMPLATE_EXAMPLE_ROWS = [
  {
    question_type: 'single_choice',
    question_text: 'What is the capital of India?',
    marks: 2, negative_marks: 0.5, difficulty: 'easy',
    explanation: 'New Delhi has been the capital since 1911.',
    options: 'Mumbai|New Delhi|Chennai|Kolkata', correct: '2',
    accepted_answers: '', case_sensitive: '', numerical_value: '', numerical_tolerance: '',
  },
  {
    question_type: 'multiple_choice',
    question_text: 'Select all prime numbers.',
    marks: 3, negative_marks: 0, difficulty: 'medium', explanation: '',
    options: '2|3|4|5', correct: '1,2,4',
    accepted_answers: '', case_sensitive: '', numerical_value: '', numerical_tolerance: '',
  },
  {
    question_type: 'true_false',
    question_text: 'FM stands for Facilities Management.',
    marks: 1, negative_marks: 0, difficulty: 'easy', explanation: '',
    options: '', correct: 'true',
    accepted_answers: '', case_sensitive: '', numerical_value: '', numerical_tolerance: '',
  },
  {
    question_type: 'short_text',
    question_text: 'Name the standard that covers facility management (ISO number).',
    marks: 2, negative_marks: 0, difficulty: 'medium', explanation: '',
    options: '', correct: '',
    accepted_answers: 'ISO 41001|41001', case_sensitive: 'no', numerical_value: '', numerical_tolerance: '',
  },
  {
    question_type: 'fill_blank',
    question_text: 'FM stands for _____ Management.',
    marks: 1, negative_marks: 0, difficulty: 'easy', explanation: '',
    options: '', correct: '',
    accepted_answers: 'Facilities|Facility', case_sensitive: '', numerical_value: '', numerical_tolerance: '',
  },
  {
    question_type: 'numerical',
    question_text: 'What is 2 + 2?',
    marks: 1, negative_marks: 0, difficulty: 'easy', explanation: '',
    options: '', correct: '', accepted_answers: '', case_sensitive: '',
    numerical_value: 4, numerical_tolerance: 0,
  },
  {
    question_type: 'long_answer',
    question_text: 'Explain the main responsibilities of a facilities manager.',
    marks: 10, negative_marks: 0, difficulty: 'hard', explanation: '',
    options: '', correct: '', accepted_answers: '', case_sensitive: '',
    numerical_value: '', numerical_tolerance: '',
  },
];

function truthy(v) {
  return /^(y|yes|true|1)$/i.test(String(v || '').trim());
}

function splitPipes(v) {
  return String(v || '')
    .split('|')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

// Convert a raw CSV row -> the question payload the API expects, plus a list
// of human-readable errors. Mirrors the server-side validator exactly.
function normaliseRow(raw) {
  const errors = [];
  const get = (k) => String(raw[k] == null ? '' : raw[k]).trim();

  const question_type = get('question_type').toLowerCase();
  const question_text = get('question_text');
  if (!VALID_TYPES.includes(question_type)) {
    errors.push(`Unknown question_type "${get('question_type')}". Use one of: ${VALID_TYPES.join(', ')}.`);
  }
  if (!question_text) errors.push('question_text is empty.');

  const marksRaw = get('marks');
  const marks = marksRaw === '' ? 1 : Number(marksRaw);
  if (isNaN(marks) || marks < 0) errors.push('marks must be a number ≥ 0.');

  const negRaw = get('negative_marks');
  const negative_marks = negRaw === '' ? 0 : Number(negRaw);
  if (isNaN(negative_marks) || negative_marks < 0) errors.push('negative_marks must be a number ≥ 0.');
  if (!isNaN(marks) && !isNaN(negative_marks) && negative_marks > marks) {
    errors.push('negative_marks cannot exceed marks.');
  }

  const difficultyRaw = get('difficulty').toLowerCase();
  let difficulty_level = null;
  if (difficultyRaw) {
    if (['easy', 'medium', 'hard'].includes(difficultyRaw)) difficulty_level = difficultyRaw;
    else errors.push('difficulty must be easy, medium or hard (or left blank).');
  }

  const payload = {
    question_type,
    question_text,
    marks: isNaN(marks) ? 1 : marks,
    negative_marks: isNaN(negative_marks) ? 0 : negative_marks,
    difficulty_level,
    explanation: get('explanation') || null,
  };

  if (question_type === 'single_choice' || question_type === 'multiple_choice') {
    const opts = splitPipes(raw.options);
    if (opts.length < 2) errors.push('options: provide at least 2 choices separated by "|".');
    const correctIdx = get('correct')
      .split(',')
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !isNaN(n) && n >= 1);
    if (correctIdx.length === 0) errors.push('correct: give the 1-based position(s) of the correct option(s).');
    if (question_type === 'single_choice' && correctIdx.length !== 1) {
      errors.push('single_choice must have exactly one correct option.');
    }
    correctIdx.forEach((n) => { if (n > opts.length) errors.push(`correct: position ${n} is beyond the number of options.`); });
    payload.options = opts.map((text, i) => ({ text, is_correct: correctIdx.includes(i + 1) }));
  } else if (question_type === 'true_false') {
    const opts = splitPipes(raw.options).length ? splitPipes(raw.options) : ['True', 'False'];
    if (opts.length !== 2) errors.push('true_false must have exactly two options (leave "options" blank for True|False).');
    const c = get('correct').toLowerCase();
    let correctIdx = [];
    if (c === 'true') correctIdx = [1];
    else if (c === 'false') correctIdx = [2];
    else if (c === '1' || c === '2') correctIdx = [Number(c)];
    else errors.push('correct: for true_false use true / false (or 1 / 2).');
    payload.options = opts.map((text, i) => ({ text, is_correct: correctIdx.includes(i + 1) }));
  } else if (question_type === 'short_text' || question_type === 'fill_blank') {
    const accepted = splitPipes(raw.accepted_answers);
    if (accepted.length < 1) errors.push('accepted_answers: provide at least one answer separated by "|".');
    payload.accepted_answers = accepted;
    payload.case_sensitive = truthy(raw.case_sensitive);
  } else if (question_type === 'numerical') {
    const numRaw = get('numerical_value');
    if (numRaw === '' || isNaN(Number(numRaw))) errors.push('numerical_value must be a number.');
    else payload.numerical_correct_value = Number(numRaw);
    const tolRaw = get('numerical_tolerance');
    const tol = tolRaw === '' ? 0 : Number(tolRaw);
    if (isNaN(tol) || tol < 0) errors.push('numerical_tolerance must be a number ≥ 0.');
    payload.numerical_tolerance = isNaN(tol) ? 0 : tol;
  }

  return { payload, errors };
}

function typeLabel(t) {
  return {
    single_choice: 'Single Choice', multiple_choice: 'Multiple Choice', true_false: 'True / False',
    short_text: 'Short Text', long_answer: 'Long Answer', fill_blank: 'Fill Blank', numerical: 'Numerical',
  }[t] || t;
}

function MockExamBulkUpload({ bankId, onImported, onClose }) {
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState([]); // { line, raw, payload, errors, type, text }
  const [parseError, setParseError] = useState('');
  const [serverErrors, setServerErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [done, setDone] = useState('');

  const getToken = () => JSON.parse(sessionStorage.getItem('adminSession'))?.token || null;

  const downloadTemplate = () => {
    const csv = Papa.unparse({ fields: TEMPLATE_HEADERS, data: TEMPLATE_EXAMPLE_ROWS });
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mock-exam-questions-template.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    setParseError('');
    setServerErrors([]);
    setDone('');
    setRows([]);
    if (!file) { setFileName(''); return; }
    setFileName(file.name);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: 'greedy',
      transformHeader: (h) => h.trim().toLowerCase(),
      complete: (result) => {
        const data = result.data || [];
        if (data.length === 0) {
          setParseError('No data rows found. Fill in the template below the header row.');
          return;
        }
        const missing = TEMPLATE_HEADERS.filter((h) => !(result.meta.fields || []).includes(h));
        if (missing.length === TEMPLATE_HEADERS.length) {
          setParseError('This file has none of the expected columns. Download the template and use its header row.');
          return;
        }
        const parsed = data.map((raw, i) => {
          const { payload, errors } = normaliseRow(raw);
          return {
            line: i + 1,
            payload,
            errors,
            type: payload.question_type,
            text: payload.question_text,
          };
        });
        setRows(parsed);
      },
      error: (err) => setParseError(`Could not read the file: ${err.message}`),
    });
  };

  const validRows = rows.filter((r) => r.errors.length === 0);
  const invalidRows = rows.filter((r) => r.errors.length > 0);
  const looksLikeUneditedTemplate =
    rows.length === TEMPLATE_EXAMPLE_ROWS.length &&
    rows.every((r, i) => r.text === TEMPLATE_EXAMPLE_ROWS[i].question_text);

  const handleImport = async () => {
    setImporting(true);
    setServerErrors([]);
    setDone('');
    try {
      const token = getToken();
      const res = await axios.post(
        `${apiUrl}/api/admin/mock-exams/banks/${bankId}/questions/bulk`,
        { questions: validRows.map((r) => r.payload) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        setDone(`${res.data.inserted} question(s) imported successfully.`);
        setRows([]);
        setFileName('');
        if (onImported) onImported();
      } else {
        setServerErrors(res.data.errors || []);
        setParseError(res.data.message || 'Import failed.');
      }
    } catch (err) {
      const data = err.response?.data;
      setServerErrors(data?.errors || []);
      setParseError(data?.message || 'Error importing questions.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="card mb-24">
      <div className="card-header border-bottom border-gray-100 d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Bulk Upload Questions</h5>
        {onClose && (
          <button type="button" className="btn btn-sm btn-outline-main py-4 px-12" onClick={onClose}>
            <i className="ph ph-x me-1" /> Close
          </button>
        )}
      </div>
      <div className="card-body">
        <div className="d-flex flex-wrap align-items-center gap-12 mb-16">
          <button type="button" className="btn btn-outline-main rounded-pill py-9" onClick={downloadTemplate}>
            <i className="ph ph-download-simple me-2" /> Download CSV Template
          </button>
          <label className="btn btn-main rounded-pill py-9 mb-0" style={{ cursor: 'pointer' }}>
            <i className="ph ph-upload-simple me-2" /> Choose CSV File
            <input type="file" accept=".csv,text/csv" hidden onChange={handleFile} disabled={importing} />
          </label>
          {fileName && <span className="text-14 text-gray-500">{fileName}</span>}
        </div>

        <details className="mb-16">
          <summary className="text-14 fw-semibold text-main-600" style={{ cursor: 'pointer' }}>
            How to fill the template
          </summary>
          <div className="mt-8 text-13 text-gray-600">
            <p className="mb-8">The template has one example row per question type — replace them with your own questions. Column reference:</p>
            <div className="table-responsive">
              <table className="table table-sm">
                <tbody>
                  <tr><td className="fw-semibold">question_type</td><td>single_choice · multiple_choice · true_false · short_text · long_answer · fill_blank · numerical</td></tr>
                  <tr><td className="fw-semibold">question_text</td><td>The question. Wrap in quotes if it contains a comma.</td></tr>
                  <tr><td className="fw-semibold">marks</td><td>Number ≥ 0. Blank = 1.</td></tr>
                  <tr><td className="fw-semibold">negative_marks</td><td>Number ≥ 0 and not more than <em>marks</em>. Blank = 0.</td></tr>
                  <tr><td className="fw-semibold">difficulty</td><td>easy / medium / hard, or blank.</td></tr>
                  <tr><td className="fw-semibold">explanation</td><td>Optional. Shown to students after the exam.</td></tr>
                  <tr><td className="fw-semibold">options</td><td>Choice questions only. Separate choices with <code>|</code> — e.g. <code>Mumbai|New Delhi|Chennai</code>. For true_false leave blank (uses True|False).</td></tr>
                  <tr><td className="fw-semibold">correct</td><td>Choice questions only. 1-based position(s) of the correct option(s), comma-separated — e.g. <code>2</code> or <code>1,2,4</code>. For true_false use <code>true</code> / <code>false</code>.</td></tr>
                  <tr><td className="fw-semibold">accepted_answers</td><td>short_text / fill_blank only. Acceptable answers separated by <code>|</code> — e.g. <code>New Delhi|Delhi</code>.</td></tr>
                  <tr><td className="fw-semibold">case_sensitive</td><td>short_text only. <code>yes</code> to match exact case, otherwise blank/no.</td></tr>
                  <tr><td className="fw-semibold">numerical_value</td><td>numerical only. The correct number.</td></tr>
                  <tr><td className="fw-semibold">numerical_tolerance</td><td>numerical only. ± range accepted as correct. Blank = 0 (exact).</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </details>

        {parseError && <div className="alert alert-danger">{parseError}</div>}
        {done && <div className="alert alert-success">{done}</div>}

        {serverErrors.length > 0 && (
          <div className="alert alert-danger">
            <div className="fw-semibold mb-4">The server rejected the import:</div>
            <ul className="mb-0">
              {serverErrors.slice(0, 20).map((e, i) => (
                <li key={i}>Row {e.row}: {e.message}</li>
              ))}
              {serverErrors.length > 20 && <li>…and {serverErrors.length - 20} more.</li>}
            </ul>
          </div>
        )}

        {rows.length > 0 && (
          <>
            {looksLikeUneditedTemplate && (
              <div className="alert alert-warning">
                This looks like the unedited template. Replace the example rows with your own questions before importing.
              </div>
            )}
            <div className={`alert ${invalidRows.length === 0 ? 'alert-success' : 'alert-warning'}`}>
              <strong>{validRows.length}</strong> of {rows.length} row(s) are ready to import.
              {invalidRows.length > 0 && (
                <> <strong>{invalidRows.length}</strong> row(s) have errors (highlighted below) and will be skipped.</>
              )}
            </div>

            <div className="table-responsive mb-16" style={{ maxHeight: 380, overflowY: 'auto' }}>
              <table className="table table-sm table-striped align-middle">
                <thead>
                  <tr>
                    <th className="text-13 text-gray-300" style={{ width: 50 }}>Row</th>
                    <th className="text-13 text-gray-300" style={{ width: 90 }}>Status</th>
                    <th className="text-13 text-gray-300" style={{ width: 130 }}>Type</th>
                    <th className="text-13 text-gray-300">Question / Problem</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.line} style={r.errors.length ? { background: '#fff4f4' } : undefined}>
                      <td className="text-13">{r.line}</td>
                      <td>
                        {r.errors.length === 0 ? (
                          <span className="badge bg-success-subtle text-success-emphasis text-12">OK</span>
                        ) : (
                          <span className="badge bg-danger-subtle text-danger-emphasis text-12">Error</span>
                        )}
                      </td>
                      <td className="text-13">{typeLabel(r.type)}</td>
                      <td className="text-13">
                        <div className="text-truncate" style={{ maxWidth: 520 }} title={r.text}>{r.text || <em className="text-gray-400">(no text)</em>}</div>
                        {r.errors.length > 0 && (
                          <div className="text-danger text-12 mt-2">{r.errors.join(' ')}</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-end gap-8">
              <button type="button" className="btn btn-outline-main rounded-pill py-9" onClick={() => { setRows([]); setFileName(''); }}>
                Clear
              </button>
              <button
                type="button"
                className="btn btn-main rounded-pill py-9"
                disabled={importing || validRows.length === 0}
                onClick={handleImport}
              >
                {importing ? 'Importing…' : `Import ${validRows.length} Question${validRows.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default MockExamBulkUpload;
